import React, { useCallback } from 'react'
import { update, deepUpdate } from 'immupdate'
import { ColItem, OptionItem } from '../interfaces'
import { v4 as uuid } from 'uuid'
import {
  Button,
  Col,
  Flex,
  Input,
  Row,
  Select,
  Space,
  Typography,
  Upload,
  UploadProps,
  message,
} from 'antd'
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import axios, { AxiosError, AxiosResponse } from 'axios'

const INPUT_TYPE_OPTIONS = [
  'text',
  'select',
  'entier',
  'decimal',
  'multiple-labels',
  'text-lecture-seule',
] as const

const uploadProps: UploadProps = {
  name: 'file',
  headers: {
    authorization: 'authorization-text',
  },
}

interface CustomColProps {
  updateCol: (col: ColItem) => void
  deleteCol: (col: ColItem) => void
  col: ColItem
}

interface OptionRowProps {
  option: OptionItem
  onNameChange: (value: string, id: string) => void
  onLabelChange: (value: string, id: string) => void
  onDelete: (id: string) => void
}

function OptionRow({ option, onNameChange, onLabelChange, onDelete }: OptionRowProps) {
  return (
    <Flex gap={8} align="center">
      <Input
        placeholder="Option"
        value={option.name}
        onChange={(e) => onNameChange(e.target.value, option.id)}
      />
      <Input
        placeholder="Label"
        value={option.label}
        onChange={(e) => onLabelChange(e.target.value, option.id)}
      />
      <Button type="primary" danger icon={<DeleteOutlined />} onClick={() => onDelete(option.id)}>
        Supprimer
      </Button>
    </Flex>
  )
}

interface ColSuppRowProps {
  suppInfo: OptionItem
  onChange: (value: string, id: string) => void
  onDelete: (id: string) => void
}

function ColSuppRow({ suppInfo, onChange, onDelete }: ColSuppRowProps) {
  return (
    <Flex gap={8} align="center">
      <Input
        placeholder={"Ex: Chiffre d'affaire"}
        value={suppInfo.name}
        onChange={(e) => onChange(e.target.value, suppInfo.id)}
      />
      <Button type="primary" danger icon={<DeleteOutlined />} onClick={() => onDelete(suppInfo.id)}>
        Supprimer
      </Button>
    </Flex>
  )
}

interface OptionsListProps {
  options: OptionItem[]
  onNameChange: (value: string, id: string) => void
  onLabelChange: (value: string, id: string) => void
  onDelete: (id: string) => void
  onAdd: () => void
  addButtonLabel?: string
}

function OptionsList({
  options,
  onNameChange,
  onLabelChange,
  onDelete,
  onAdd,
  addButtonLabel = 'Ajouter une option',
}: OptionsListProps) {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text>Options :</Typography.Text>
      <Flex vertical gap={8} style={{ maxHeight: 300, overflowY: 'auto' }}>
        {options.map((option) => (
          <OptionRow
            key={option.id}
            option={option}
            onNameChange={onNameChange}
            onLabelChange={onLabelChange}
            onDelete={onDelete}
          />
        ))}
      </Flex>
      <Button type="default" icon={<PlusOutlined />} onClick={onAdd}>
        {addButtonLabel}
      </Button>
    </Space>
  )
}

export function CustomCol({ col, updateCol, deleteCol }: CustomColProps) {
  const handleChange = useCallback(
    (id: 'name' | 'inputtype', value: string) => {
      const newCol = deepUpdate(col).at('custom').abortIfUndef().at(id).set(value)
      updateCol(newCol)
    },
    [col, updateCol],
  )

  const addOption = useCallback(
    (optionName?: string) => {
      if (col.custom) {
        const newOption = { id: uuid(), name: optionName ? optionName : '' }
        const newOptions = (col.custom.options || []).concat([newOption])
        const newCol = deepUpdate(col).at('custom').abortIfUndef().at('options').set(newOptions)
        updateCol(newCol)
      }
    },
    [col, updateCol],
  )

  const addMultipleOptions = useCallback(
    (options: string[]) => {
      if (col.custom) {
        const newOptions = options.map((option) => ({
          id: uuid(),
          name: option || '',
        }))
        const updatedOptions = (col.custom.options || []).concat(newOptions)
        const newCol = deepUpdate(col).at('custom').abortIfUndef().at('options').set(updatedOptions)
        updateCol(newCol)
      }
    },
    [col, updateCol],
  )

  const addColSupp = useCallback(
    (colSuppName?: string) => {
      if (col.custom) {
        const newColSuppItem = { id: uuid(), name: colSuppName ? colSuppName : '' }
        const newColSupp = (col.custom.colSupp || []).concat([newColSuppItem])
        const newColData = deepUpdate(col).at('custom').abortIfUndef().at('colSupp').set(newColSupp)
        updateCol(newColData)
      }
    },
    [col, updateCol],
  )

  const handleColSuppChange = useCallback(
    (value: string, id: string) => {
      if (col.custom) {
        const newColSupp = (col.custom.colSupp || []).map((e: OptionItem) =>
          e.id == id ? update(e, { name: value }) : e,
        )
        const newCol = deepUpdate(col).at('custom').abortIfUndef().at('colSupp').set(newColSupp)
        updateCol(newCol)
      }
    },
    [col, updateCol],
  )

  const deleteColSupp = useCallback(
    (id: string) => {
      if (col.custom) {
        const newColSup = col.custom.colSupp?.filter((e: OptionItem) => e.id != id) || []
        const newCol = deepUpdate(col).at('custom').abortIfUndef().at('colSupp').set(newColSup)
        updateCol(newCol)
      }
    },
    [col, updateCol],
  )

  const deleteOption = useCallback(
    (id: string) => {
      if (col.custom) {
        const newOptions = col.custom.options?.filter((e: OptionItem) => e.id != id) || []
        const newCol = deepUpdate(col).at('custom').abortIfUndef().at('options').set(newOptions)
        updateCol(newCol)
      }
    },
    [col, updateCol],
  )

  const handleOptionNameChange = useCallback(
    (value: string, id: string) => {
      if (col.custom) {
        const newOptions = (col.custom.options || []).map((e: OptionItem) =>
          e.id == id ? update(e, { name: value }) : e,
        )
        const newCol = deepUpdate(col).at('custom').abortIfUndef().at('options').set(newOptions)
        updateCol(newCol)
      }
    },
    [col, updateCol],
  )

  const handleOptionLabelChange = useCallback(
    (value: string, id: string) => {
      const endValue = value.trim().length > 0 ? value : null
      if (col.custom) {
        const newOptions = (col.custom.options || []).map((e: OptionItem) =>
          e.id == id ? update(e, { label: endValue ?? undefined }) : e,
        )
        const newCol = deepUpdate(col).at('custom').abortIfUndef().at('options').set(newOptions)
        updateCol(newCol)
      }
    },
    [col, updateCol],
  )

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    const { status } = info.file
    if (status === 'done') {
      message.success(`${info.file.name} file uploaded successfully`)
      const response = info.file.response
      if (response && typeof response === 'string') {
        axios.get(response)
        .then((response: AxiosResponse<string[]>) => {
          addMultipleOptions(response.data as string[])
        })
        .catch((error: AxiosError) => {
          console.error('Error fetching competitors:', error)
        })
      } else {
        addMultipleOptions(response)
      }
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} file upload failed.`)
    }
  }

  if (!col.custom) {
    return null
  }

  return (
    <div style={{ display: 'inline-block', width: 'calc(100% - 40px)' }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={10}>
          <Input
            placeholder="Nom colonne"
            value={col.custom.name}
            onChange={(e) => handleChange('name', e.target.value)}
          />
        </Col>
        <Col xs={24} sm={10}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Typography.Text>Type de champ</Typography.Text>
            <Select
              style={{ width: '100%' }}
              value={col.custom.inputtype}
              onChange={(value) => handleChange('inputtype', value)}
              options={INPUT_TYPE_OPTIONS.map((type) => ({ label: type, value: type }))}
            />
          </Space>
        </Col>
        {col.coltype === 'custom' && (
          <Col xs={24} sm={4}>
            <Button danger type="primary" onClick={() => deleteCol(col)}>
              Supprimer
            </Button>
          </Col>
        )}
      </Row>

      {col.custom.inputtype == 'select' && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <OptionsList
              options={col.custom.options || []}
              onNameChange={handleOptionNameChange}
              onLabelChange={handleOptionLabelChange}
              onDelete={deleteOption}
              onAdd={() => addOption()}
            />
          </Col>
        </Row>
      )}

      {col.custom.inputtype == 'multiple-labels' && (
        <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 16 }}>
          <div>
            <Typography.Text>Informations supplémentaires (max. 4):</Typography.Text>
            <Flex vertical gap={8} style={{ maxHeight: 300, overflowY: 'auto', marginTop: 8 }}>
              {col.custom.colSupp?.map((suppInfo: OptionItem) => (
                <ColSuppRow
                  key={suppInfo.id}
                  suppInfo={suppInfo}
                  onChange={handleColSuppChange}
                  onDelete={deleteColSupp}
                />
              ))}
            </Flex>
            {(col.custom.colSupp === undefined || col.custom.colSupp.length < 4) && (
              <Button
                type="default"
                icon={<PlusOutlined />}
                style={{ marginTop: 8 }}
                onClick={() => addColSupp()}
              >
                Ajouter une information supplémentaire
              </Button>
            )}
          </div>

          <div>
            <Flex gap={10} align="center" wrap="wrap">
              <Typography.Text>Options :</Typography.Text>
              <Upload
                {...uploadProps}
                action={document.location.origin + `/cnegoces/uploadMultipleLabelsFile`}
                onChange={handleUploadChange}
              >
                <Button icon={<UploadOutlined />}>Cliquer pour charger des options</Button>
              </Upload>
            </Flex>
            <Flex vertical gap={8} style={{ maxHeight: 300, overflowY: 'auto', marginTop: 8 }}>
              {(col.custom.options || []).map((option: OptionItem) => (
                <OptionRow
                  key={`label-${col.name}-${option.id}`}
                  option={option}
                  onNameChange={handleOptionNameChange}
                  onLabelChange={handleOptionLabelChange}
                  onDelete={deleteOption}
                />
              ))}
            </Flex>
            <Button
              type="default"
              icon={<PlusOutlined />}
              style={{ marginTop: 8 }}
              onClick={() => addOption()}
            >
              Ajouter une option
            </Button>
          </div>
        </Space>
      )}
    </div>
  )
}

export default CustomCol
